import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useFlags } from '@atlaskit/flag';
import ErrorIcon from '@atlaskit/icon/core/migration/status-error--error';
import { R400 } from '@atlaskit/theme/colors';
import { fireOperationalAnalytics, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { messages } from './messages';
import { copyToAttachment, getAttachmentsSize, JSMAttachmentValidationError } from './utils';
export var useAttachments = function useAttachments(attachmentArgs) {
  var mediaUploadAndViewContexts = attachmentArgs.mediaUploadAndViewContexts,
    projectId = attachmentArgs.projectId,
    portalId = attachmentArgs.portalId,
    maxAllowedTotalAttachmentsSize = attachmentArgs.maxAllowedTotalAttachmentsSize,
    onAttachmentSizeValidation = attachmentArgs.onAttachmentSizeValidation,
    onAttachmentUpdate = attachmentArgs.onAttachmentUpdate,
    numberOfAttachmentsOnForm = attachmentArgs.numberOfAttachmentsOnForm;
  var _useFlags = useFlags(),
    showFlag = _useFlags.showFlag;
  if (!mediaUploadAndViewContexts) {
    throw new Error('mediaContext is null!');
  }
  var mediaUploadContext = mediaUploadAndViewContexts.uploadContext;
  if (!mediaUploadContext) {
    throw new Error('mediaUploadContext is null!');
  }
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    attachments = _useState2[0],
    setAttachments = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    uploadedFiles = _useState4[0],
    setUploadedFiles = _useState4[1];
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    existingFiles = _useState6[0],
    setExistingFiles = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isOnDrag = _useState8[0],
    setIsOnDrag = _useState8[1];
  var _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    isUploading = _useState0[0],
    setUploading = _useState0[1];
  var _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    isDefaultValueInitialized = _useState10[0],
    setDefaultValueInitialized = _useState10[1];
  var stateRef = useRef();
  stateRef.current = uploadedFiles;
  var mediaUploadClientConfig = useMemo(function () {
    return {
      authProvider: function authProvider() {
        return Promise.resolve({
          baseUrl: mediaUploadContext.serviceHost,
          clientId: mediaUploadContext.clientId,
          token: mediaUploadContext.token
        });
      },
      useSha256ForUploads: true
    };
  }, [mediaUploadContext]);
  var uploadConfig = useMemo(function () {
    return {
      uploadParams: {
        collection: mediaUploadContext.collection
      }
    };
  }, [mediaUploadContext.collection]);
  var mediaReadContext = mediaUploadAndViewContexts.viewContext;
  var mediaReadClientConfig = useMemo(function () {
    if (mediaReadContext) {
      return {
        authProvider: function authProvider() {
          return Promise.resolve({
            baseUrl: mediaReadContext.serviceHost,
            clientId: mediaReadContext.clientId,
            token: mediaReadContext.token || ''
          });
        },
        useSha256ForUploads: true
      };
    }
    return mediaUploadClientConfig;
  }, [mediaReadContext, mediaUploadClientConfig]);
  useEffect(function () {
    setUploadedFiles(attachments.map(function (attachment) {
      return attachment.mediaFile;
    }));
    setUploading(attachments.some(function (attachment) {
      return attachment.isUploading;
    }));
  }, [attachments]);
  useEffect(function () {
    onAttachmentUpdate === null || onAttachmentUpdate === void 0 || onAttachmentUpdate(uploadedFiles.concat(existingFiles));
  }, [uploadedFiles, existingFiles, onAttachmentUpdate]);
  var showUploadError = useCallback(function (message, messageDescriptor) {
    var description = message ? message : /*#__PURE__*/React.createElement(FormattedMessage, messages.uploadErrorDescription);
    if (messageDescriptor) {
      description = /*#__PURE__*/React.createElement(FormattedMessage, messageDescriptor);
    }
    return showFlag({
      icon: /*#__PURE__*/React.createElement(ErrorIcon, {
        spacing: "spacious",
        label: "Error",
        color: "var(--ds-icon-accent-red, ".concat(R400, ")")
      }),
      title: /*#__PURE__*/React.createElement(FormattedMessage, messages.uploadError),
      description: description
    });
  }, [showFlag]);
  var onUploadStart = useCallback(function (uploadStartPayload) {
    var currentFiles = stateRef.current ? stateRef.current : [];
    var totalAttachments = [].concat(_toConsumableArray(uploadStartPayload.files), _toConsumableArray(currentFiles));
    var totalAttachmentsOnForm = numberOfAttachmentsOnForm ? numberOfAttachmentsOnForm + uploadStartPayload.files.length : totalAttachments.length;
    if (totalAttachmentsOnForm > 50) {
      showUploadError(undefined, messages.uploadErrorForSizeLimit);
      return;
    }
    if (maxAllowedTotalAttachmentsSize === undefined || getAttachmentsSize(totalAttachments) <= maxAllowedTotalAttachmentsSize) {
      if (onAttachmentSizeValidation) {
        onAttachmentSizeValidation(false);
      }
      setAttachments(function (currentAttachments) {
        return currentAttachments.concat(uploadStartPayload.files.map(function (mediaFile) {
          return {
            mediaFile: mediaFile,
            isUploading: true
          };
        }));
      });
    } else if (onAttachmentSizeValidation) {
      onAttachmentSizeValidation(true);
    }
  }, [onAttachmentSizeValidation, maxAllowedTotalAttachmentsSize, numberOfAttachmentsOnForm, showUploadError]);
  var onUploadError = useCallback(function (uploadError) {
    var _uploadError$error, _uploadError$error2, _uploadError$traceCon;
    setAttachments(function (currentAttachments) {
      return currentAttachments.filter(function (attachment) {
        return attachment.mediaFile.id !== uploadError.fileId;
      });
    });
    showUploadError();
    var isTokenExpired = !(mediaUploadContext.tokenIssueTimestamp + mediaUploadContext.tokenLifespanInMs > Date.now());
    var timeUntilExpiry = mediaUploadContext.tokenIssueTimestamp + mediaUploadContext.tokenLifespanInMs - Date.now();
    fireOperationalAnalytics(createAnalyticsEvent({}), "".concat(ActionSubject.UPLOAD_ISSUE_FORM_ATTACHMENTS, " ").concat(Action.FAILED), {
      fileId: uploadError.fileId,
      error: uploadError.error.description,
      rawErrorMessage: (_uploadError$error = uploadError.error) === null || _uploadError$error === void 0 || (_uploadError$error = _uploadError$error.rawError) === null || _uploadError$error === void 0 ? void 0 : _uploadError$error.message,
      rawErrorStack: (_uploadError$error2 = uploadError.error) === null || _uploadError$error2 === void 0 || (_uploadError$error2 = _uploadError$error2.rawError) === null || _uploadError$error2 === void 0 ? void 0 : _uploadError$error2.stack,
      traceId: uploadError === null || uploadError === void 0 || (_uploadError$traceCon = uploadError.traceContext) === null || _uploadError$traceCon === void 0 ? void 0 : _uploadError$traceCon.traceId,
      isTokenExpired: isTokenExpired,
      timeUntilExpiry: timeUntilExpiry
    });
  }, [createAnalyticsEvent, showUploadError, mediaUploadContext]);
  var onUploadEnd = useCallback(function (uploadEndPayload) {
    _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var mediaFile;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            mediaFile = uploadEndPayload.file;
            _context.prev = 1;
            if (!projectId) {
              _context.next = 7;
              break;
            }
            _context.next = 5;
            return copyToAttachment(projectId, mediaFile, true);
          case 5:
            _context.next = 10;
            break;
          case 7:
            if (!portalId) {
              _context.next = 10;
              break;
            }
            _context.next = 10;
            return copyToAttachment(portalId, mediaFile, false);
          case 10:
            _context.next = 17;
            break;
          case 12:
            _context.prev = 12;
            _context.t0 = _context["catch"](1);
            // If registration fails, remove attachment from the component and show an error
            setAttachments(function (currentAttachments) {
              return currentAttachments.filter(function (attachment) {
                return attachment.mediaFile.id !== mediaFile.id;
              });
            });
            if (_context.t0 instanceof JSMAttachmentValidationError) {
              showUploadError(_context.t0.message);
            } else {
              showUploadError();
            }
            fireOperationalAnalytics(createAnalyticsEvent({}), "".concat(ActionSubject.UPLOAD_ISSUE_FORM_ATTACHMENTS, " ").concat(Action.STOPPED), {
              fileId: uploadEndPayload.file.id,
              reason: _context.t0 instanceof JSMAttachmentValidationError ? 'validation' : 'other',
              error: _context.t0.message ? _context.t0.message.replaceAll(uploadEndPayload.file.name, 'the file') : 'unknown',
              traceId: _context.t0.traceId
            });
          case 17:
            fireUIAnalytics(createAnalyticsEvent({
              actionSubject: ActionSubject.UPLOAD_ISSUE_FORM_ATTACHMENTS,
              action: Action.FINISHED
            }), projectId ? {
              projectId: projectId,
              fileId: mediaFile.id
            } : {
              portalId: portalId,
              fileId: mediaFile.id
            });

            // Set isUploading to false
            setAttachments(function (currentAttachments) {
              var isAttachmentPresentInCurrentAttachments = currentAttachments.find(function (attachment) {
                return attachment.mediaFile.id === mediaFile.id;
              });
              if (isAttachmentPresentInCurrentAttachments) {
                return currentAttachments.filter(function (attachment) {
                  return attachment.mediaFile.id !== mediaFile.id;
                }).concat([{
                  mediaFile: mediaFile,
                  isUploading: false
                }]);
              }
              return currentAttachments;
            });
          case 19:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[1, 12]]);
    }))();
  }, [createAnalyticsEvent, portalId, projectId, showUploadError]);
  var onRemoveFile = function onRemoveFile(fileToBeRemoved) {
    setAttachments(function (currentAttachments) {
      return currentAttachments.filter(function (attachment) {
        return attachment.mediaFile.id !== fileToBeRemoved.details.id;
      });
    });
    setExistingFiles(function (existingFiles) {
      return existingFiles.filter(function (mediaFile) {
        return mediaFile.id !== fileToBeRemoved.details.id;
      });
    });
    if (onAttachmentSizeValidation) {
      onAttachmentSizeValidation(false);
    }
  };
  return {
    uploadedFiles: uploadedFiles,
    existingFiles: existingFiles,
    onUploadStart: onUploadStart,
    onUploadError: onUploadError,
    mediaUploadClientConfig: mediaUploadClientConfig,
    mediaReadClientConfig: mediaReadClientConfig,
    uploadConfig: uploadConfig,
    onRemoveFile: onRemoveFile,
    onUploadEnd: onUploadEnd,
    isUploading: isUploading,
    isDefaultValueInitialized: isDefaultValueInitialized,
    setDefaultValueInitialized: setDefaultValueInitialized,
    setExistingFiles: setExistingFiles,
    setAttachments: setAttachments,
    isOnDrag: isOnDrag,
    setIsOnDrag: setIsOnDrag
  };
};